/**
 * Main Collection Product Grid - Global Manager
 * Shared JavaScript for main-collection-product-grid sections
 */

window.MainCollectionProductGrid = window.MainCollectionProductGrid || (function() {
  'use strict';
  
  // ========== Passive Event Listener 호환성 체크 ==========
  let passiveSupported = false;
  try {
    const options = {
      get passive() {
        passiveSupported = true;
        return false;
      }
    };
    window.addEventListener('test', null, options);
    window.removeEventListener('test', null, options);
  } catch (err) {
    passiveSupported = false;
  }
  
  // ========== 설정 상수 ==========
  const CONFIG = {
    REFRESH_DELAY: 100,
    RESIZE_DEBOUNCE: 150,
    REINIT_DELAY: 100,
    INTERSECTION_MARGIN: '50px'
  };
  
  const instances = {};
  
  function setupLazyInit(sectionId) {
    const instance = instances[sectionId];
    if (!instance) return;
    
    // DOM 캐싱
    if (!instance.splideEl) {
      instance.splideEl = document.querySelector(instance.splideSelector);
    }
    const splideEl = instance.splideEl;
    
    if (!splideEl) return;
    
    if (splideEl.dataset.splideMounted === 'true') {
      return;
    }
    
    // IntersectionObserver 사용 (요소가 보이면 초기화)
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          // 요소가 이미 뷰포트에 있거나 교차하는 경우
          if (entry.isIntersecting || entry.intersectionRatio > 0) {
            initProductGridSplide(sectionId);
            observer.disconnect();
          }
        });
      }, {
        rootMargin: CONFIG.INTERSECTION_MARGIN,
        threshold: 0
      });
      
      observer.observe(splideEl);
      
      // Defer로 인해 이미 뷰포트에 있을 수 있으므로 즉시 체크
      setTimeout(function() {
        const rect = splideEl.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          initProductGridSplide(sectionId);
          observer.disconnect();
        }
      }, 50);
    } else {
      initProductGridSplide(sectionId);
    }
  }
  
  function initProductGridSplide(sectionId) {
    const instance = instances[sectionId];
    if (!instance) return;
    
    const SETTINGS = instance.settings;
    
    // DOM 캐싱 사용
    if (!instance.splideEl) {
      instance.splideEl = document.querySelector(instance.splideSelector);
    }
    const splideEl = instance.splideEl;
    
    if (!splideEl || !window.Splide) {
      return;
    }
    
    if (splideEl.dataset.splideMounted === 'true' || instance.splide) {
      return;
    }
    
    const viewportWidth = window.innerWidth;
    const isMobile = viewportWidth <= 749;
    
    // querySelectorAll 결과 캐싱
    const slides = splideEl.querySelectorAll('.splide__slide:not(.splide__slide--clone)');
    const totalSlides = slides.length;
    
    const isForcedMode = SETTINGS.enableForced 
      && SETTINGS.enableDesktop
      && viewportWidth > 749 
      && viewportWidth <= SETTINGS.forcedBreakpoint
      && totalSlides > SETTINGS.breakpointColumns;
    
    let shouldActivate = false;
    let perPage = 1;
    let loopType = 'slide';
    
    if (isForcedMode) {
      shouldActivate = true;
      perPage = SETTINGS.breakpointColumns;
      loopType = SETTINGS.desktopSliderLoop ? 'loop' : 'slide';
    } else if (isMobile && SETTINGS.enableMobile) {
      shouldActivate = true;
      perPage = SETTINGS.columnsMobile;
      loopType = SETTINGS.mobileSliderLoop ? 'loop' : 'slide';
    } else if (!isMobile && SETTINGS.enableDesktop) {
      shouldActivate = true;
      perPage = SETTINGS.columnsDesktop;
      loopType = SETTINGS.desktopSliderLoop ? 'loop' : 'slide';
    }
    
    if (shouldActivate) {
      splideEl.classList.remove('splide--disabled');
      if (!instance.splide) {
        if (totalSlides <= perPage) {
          splideEl.classList.add('splide--disabled');
          return;
        }
        
        const gapSize = isMobile ? SETTINGS.gapMobile : SETTINGS.gapDesktop;
        
        splideEl.dataset.splideMounted = 'true';

        const splideOptions = {
          type: loopType,
          perPage: perPage,
          perMove: 1,
          gap: gapSize + 'px',
          arrows: true,
          pagination: false,
          rewind: false,
          drag: true,
          // 클릭 반응성 개선
          click: true,
          dragMinThreshold: 30,
          swipeDistanceThreshold: 30,
          flickVelocityThreshold: 0.4,
          waitForTransition: false,
          releaseWheel: true,
          speed: 250,
          flickPower: 140,
          flickMaxPages: 1,
          updateOnMove: true,
          lazyLoad: 'nearby',
          preloadPages: 1,
          breakpoints: {
            768: {
              drag: true,
              click: true,
              dragMinThreshold: 30,
              waitForTransition: false,
              releaseWheel: true,
              speed: 250,
              flickPower: 140,
              flickMaxPages: 1,
              swipeDistanceThreshold: 30,
              flickVelocityThreshold: 0.4
            }
          }
        };

        instance.splide = new Splide(instance.splideSelector, splideOptions);
        instance.splide.mount();
        
        if (loopType === 'loop') {
          const focusableSelector = 'a, button, input, select, textarea';

          function updateSlideAccessibility(slide, shouldHide) {
            if (shouldHide) {
              if (!slide.hasAttribute('inert')) {
                slide.setAttribute('inert', '');
                slide.querySelectorAll(focusableSelector).forEach(function(el) {
                  el.setAttribute('tabindex', '-1');
                });
              }
            } else {
              if (slide.hasAttribute('inert')) {
                slide.removeAttribute('inert');
                slide.querySelectorAll(focusableSelector).forEach(function(el) {
                  el.removeAttribute('tabindex');
                });
              }
            }
          }

          function manageSlidesFocus() {
            requestAnimationFrame(function() {
              const allSlides = splideEl.querySelectorAll('.splide__slide');
              allSlides.forEach(function(slide) {
                const isHidden = slide.getAttribute('aria-hidden') === 'true';
                updateSlideAccessibility(slide, isHidden);
              });
            });
          }

          instance.splide.on('moved', manageSlidesFocus);
          instance.splide.on('mounted', manageSlidesFocus);
        }
        
        setTimeout(function() {
          if (instance.splide) {
            instance.splide.refresh();
          }
        }, CONFIG.REFRESH_DELAY);
      }
    } else {
      if (instance.splide) {
        instance.splide.destroy();
        instance.splide = null;
        
        if (splideEl) {
          splideEl.removeAttribute('data-splide-mounted');
        }
      }
      splideEl.classList.add('splide--disabled');
    }
  }
  
  function handleResize(sectionId) {
    const instance = instances[sectionId];
    if (!instance) return;
    
    const currentWidth = window.innerWidth;
    
    if (currentWidth !== instance.lastWidth) {
      instance.lastWidth = currentWidth;
      
      clearTimeout(instance.resizeTimer);
      instance.resizeTimer = setTimeout(function() {
        if (instance.splide) {
          instance.splide.destroy();
          instance.splide = null;
          
          // DOM 캐싱 사용
          const splideEl = instance.splideEl;
          if (splideEl) {
            splideEl.removeAttribute('data-splide-mounted');
          }
        }
        initProductGridSplide(sectionId);
      }, CONFIG.RESIZE_DEBOUNCE);
    }
  }
  
  return {
    init: function(sectionId, settings) {
      // Settings 숫자 값 파싱 (1회만 - 성능 최적화)
      const parsedSettings = {
        // Boolean values
        enableForced: settings.enableForced,
        enableMobile: settings.enableMobile,
        enableDesktop: settings.enableDesktop,
        mobileSliderLoop: settings.mobileSliderLoop,
        desktopSliderLoop: settings.desktopSliderLoop,
        // Numeric values
        columnsMobile: parseInt(settings.columnsMobile, 10) || 1,
        columnsDesktop: parseInt(settings.columnsDesktop, 10) || 3,
        breakpointColumns: parseInt(settings.breakpointColumns, 10) || 3,
        forcedBreakpoint: parseInt(settings.forcedBreakpoint, 10) || 960,
        // Gap values as numbers (px will be added when used)
        gapMobile: parseInt(settings.gapMobile, 10) || 16,
        gapDesktop: parseInt(settings.gapDesktop, 10) || 40
      };
      
      if (!instances[sectionId]) {
        instances[sectionId] = {
          splide: null,
          splideEl: null,
          splideSelector: '#product-grid-splide-' + sectionId,
          settings: parsedSettings,
          resizeTimer: null,
          lastWidth: window.innerWidth,
          resizeHandler: null,
          resizeObserver: null
        };
        
        instances[sectionId].resizeHandler = function() {
          handleResize(sectionId);
        };
        
        // ResizeObserver 사용 (fallback: resize event)
        if ('ResizeObserver' in window) {
          instances[sectionId].resizeObserver = new ResizeObserver(function() {
            handleResize(sectionId);
          });
          
          // DOM 로드 후 observe
          const checkAndObserve = function() {
            if (!instances[sectionId].splideEl) {
              instances[sectionId].splideEl = document.querySelector(instances[sectionId].splideSelector);
            }
            if (instances[sectionId].splideEl) {
              instances[sectionId].resizeObserver.observe(instances[sectionId].splideEl);
            }
          };
          
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', checkAndObserve);
          } else {
            checkAndObserve();
          }
        } else {
          // Fallback: resize event with passive
          window.addEventListener('resize', instances[sectionId].resizeHandler, passiveSupported ? { passive: true } : false);
        }
      }
      
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() {
          setupLazyInit(sectionId);
        });
      } else {
        setupLazyInit(sectionId);
      }
    },
    
    reinit: function(sectionId) {
      const instance = instances[sectionId];
      if (!instance) return;
      
      if (instance.splide) {
        instance.splide.destroy();
        instance.splide = null;
        
        // DOM 캐싱 사용
        const splideEl = instance.splideEl;
        if (splideEl) {
          splideEl.removeAttribute('data-splide-mounted');
        }
      }
      
      setTimeout(function() {
        initProductGridSplide(sectionId);
      }, CONFIG.REINIT_DELAY);
    },
    
    destroy: function(sectionId) {
      const instance = instances[sectionId];
      if (!instance) return;
      
      if (instance.splide) {
        instance.splide.destroy();
        instance.splide = null;
      }
      
      // ResizeObserver 정리
      if (instance.resizeObserver) {
        instance.resizeObserver.disconnect();
        instance.resizeObserver = null;
      }
      
      // Resize event listener 정리 (fallback)
      if (instance.resizeHandler) {
        window.removeEventListener('resize', instance.resizeHandler, passiveSupported ? { passive: true } : false);
      }
      
      delete instances[sectionId];
    }
  };
})();
